/*
 * global Sagas
 *
 * @version 1.0.0
 * @author Pietro Bertuccelli
 * @since 10-Oct-2024
 */

/*
 * global Sagas
 *
 * @version 1.0.0
 * @author Giuseppe
 * @since 31-May-2024
 */

import { put, take, call, all, takeEvery, takeLatest } from 'redux-saga/effects'
import { MAIN_ROUTES_RESET_TABS } from 'Utility/utils'
import { nullSafe, openUrl } from 'Utility/globalMethods'
import * as globalActions from 'Store/actions/global'
import * as globalApi from 'Api/global/global'
import * as amlApi from 'Api/aml/aml'
import * as apiHomepage from 'Api/homepage/homepage'
import { flowPopupMapType } from '../../utils/flowPopupMap'
import { doShowPopup } from 'Store/actions/popup'

function * getPhonePrefixesSaga () {
  yield put(globalActions.phonePrefixes.request({}, { loading: true }))
  const { error, response } = yield call(globalApi.getInternationalPrefixes, {})

  if (!error) {
    yield put(globalActions.phonePrefixes.success(response))
  } else {
    yield put(globalActions.phonePrefixes.failure(error, { hideErrorPopup: true, ...response })) // TODO: remove hideErrorPopup when BE will be ready
  }
}
function * getMinAppVersionSaga () {
  yield put(globalActions.getMinAppVersion.request({}, { loading: true }))
  const { error, response } = yield call(globalApi.getMinAppVersion, {})

  if (!error && response) {
    yield put(globalActions.getMinAppVersion.success(response))
  } else {
    yield put(globalActions.getMinAppVersion.failure(error, {}))
  }
}
function * firebaseSaveTokenSaga (payload) {
  yield put(globalActions.saveFirebaseToken.request({}, { loading: true }))
  const { error, response } = yield call(globalApi.sendFirebaseToken, { appInstanceId: payload.token, notificationsEnabled: payload.permission })

  if (!error) {
    yield put(globalActions.saveFirebaseToken.success(response))
  } else {
    yield put(globalActions.saveFirebaseToken.failure(error, {}))
  }
}

function * getAmlSurveyInfoSaga () {
  yield put(globalActions.getAmlSurveyInfo.request({}, { loading: true }))
  const { error, response } = yield call(amlApi.getSurvey, {})

  if (!error) {
    yield put(globalActions.getAmlSurveyInfo.success(response.data))
  } else {
    yield put(globalActions.getAmlSurveyInfo.failure(error, response))
  }
}

function * consumeTokenNpgSaga (payload) {
  yield put(globalActions.consumeTokenNpg.request({}, { loading: true }))
  const { error, response } = yield call(apiHomepage.getUrlForCommerceWebSSO, { ...payload })

  if (!error) {
    yield put(globalActions.consumeTokenNpg.success(response.data))
    const isFromOldNpgPage = window.location.pathname.includes('/oldnpg')
    if (window.innerWidth <= 768) {
      yield put(doShowPopup(flowPopupMapType.GO_TO_OLD_NPG_MODAL))
    } else {
      openUrl(`${payload.ssoURL}/backoffice/transactions/search`, !isFromOldNpgPage)
    }
  } else {
    yield put(globalActions.consumeTokenNpg.failure(error, response))
  }
}
function * eventsSaga (payload) {
  yield put(globalActions.events.request({}, { loading: true }))
  const { error, response } = yield call(globalApi.events, payload)

  if (!error) {
    yield put(globalActions.events.success(response))
  } else {
    yield put(globalActions.events.failure(error, { hideErrorPopup: true, ...response }))
  }
}
// @generator saga

// @generator saga:watch

function * watchEvents () {
  while (true) {
    const { payload } = yield take(globalActions.actionTypes.DO_EVENTS)
    yield call(eventsSaga, payload)
  }
}

function * watchConsumeTokenNpg () {
  while (true) {
    const { payload } = yield take(globalActions.actionTypes.DO_CONSUME_TOKEN_NPG)
    yield call(consumeTokenNpgSaga, payload)
  }
}

function * watchGetMinAppVersion () {
  while (yield take(globalActions.actionTypes.DO_GET_MIN_VERSION)) {
    yield call(getMinAppVersionSaga)
  }
}
function * watchFirebaseSaveTokenSaga () {
  while (true) {
    const { payload } = yield take(globalActions.actionTypes.DO_SAVE_FIREBASE_TOKEN)
    yield call(firebaseSaveTokenSaga, payload)
  }
}

function * watchGetAmlSurveyInfo () {
  yield takeLatest(globalActions.actionTypes.DO_GET_AML_SURVEY_INFO, getAmlSurveyInfoSaga)
}

function * watchGetPhonePrefixes () {
  while (yield take(globalActions.actionTypes.DO_PHONE_PREFIXES)) {
    yield call(getPhonePrefixesSaga)
  }
}

let previousMainRoute = null // Used to keep track of the previous main route

function * resetTabOnMainRouteChangeSaga (action) {
  const newPathname = nullSafe(() => action.payload.location.pathname, '')
  const newMainRoute = newPathname.split('/')[1] || '' // If it's root, it will be an empty string
  const locationState = nullSafe(() => action.payload.location.state, null)
  // List of main routes that should reset the tab
  const mainRoutes = MAIN_ROUTES_RESET_TABS || []

  // Reset only if:
  // 1. The main route has changed from the previous one
  // 2. The new route is among those that manage tabs
  // 3. There's no specific state in the location (to avoid interfering with back navigation)
  // 4. The previous main route is not 'transactions' to avoid resetting when navigating back from AccountedTransactionDetails
  if (
    previousMainRoute !== null &&
    previousMainRoute !== newMainRoute &&
    mainRoutes.includes(newMainRoute) &&
    locationState === null &&
    previousMainRoute !== 'transactions'
  ) {
    yield put(globalActions.doSetSelectedTab(null))
  }

  // Update the previous main route
  previousMainRoute = newMainRoute
}

function * watchRouteChange () {
  yield takeEvery('@@router/LOCATION_CHANGE', resetTabOnMainRouteChangeSaga)
}

/**
 * Root saga manages watcher lifecycle
 */
export default function * rootSaga () {
  yield all([
    watchGetPhonePrefixes(),
    watchGetMinAppVersion(),
    watchFirebaseSaveTokenSaga(),
    watchGetAmlSurveyInfo(),
    watchRouteChange(),
    watchConsumeTokenNpg(),
    watchEvents()
    // @generator saga:fork

  ])
}
